import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Props = {
  /** Optional room key — same key = same audience. Default: whole site. */
  room?: string;
  className?: string;
  variant?: "dark" | "light";
};

/**
 * Live viewer counter using Supabase Realtime presence.
 * No database table needed — presence state lives in Realtime memory.
 * Each browser tab counts as one viewer; disconnects auto-remove after ~30s.
 */
export function LiveViewers({ room = "site-presence", className = "", variant = "dark" }: Props) {
  const [count, setCount] = useState<number>(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const clientId =
      (typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)) + "-" + Date.now();

    const channel = supabase.channel(room, {
      config: { presence: { key: clientId } },
    });

    const recount = () => {
      const state = channel.presenceState();
      setCount(Object.keys(state).length);
    };

    channel
      .on("presence", { event: "sync" }, recount)
      .on("presence", { event: "join" }, recount)
      .on("presence", { event: "leave" }, recount)
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({ at: Date.now() });
          setReady(true);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [room]);

  if (!ready) return null;

  const styles =
    variant === "light"
      ? "border-border bg-card text-foreground"
      : "border-background/25 bg-background/10 text-background backdrop-blur-md";

  const soloLabel = count <= 1;

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${styles} ${className}`}
      aria-live="polite"
      title="Antal besökare som tittar just nu"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      <Eye size={12} className="opacity-80" />
      {soloLabel ? (
        <span className="opacity-90">Du tittar nu</span>
      ) : (
        <>
          <span className="tabular-nums">{count}</span>
          <span className="opacity-80">online nu</span>
        </>
      )}
    </span>
  );
}
